import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Mail, MessageSquare, Send, Users, Eye, Loader2, History } from 'lucide-react';
import { formatDateTime } from '../../lib/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AUDIENCES = [
    { value: 'active', label: 'Active students (enrolled)' },
    { value: 'waiting', label: 'Waiting list' },
    { value: 'pending', label: 'Pending list (awaiting registration fee)' },
    { value: 'activity', label: 'Registered in an activity / event' },
];
const CHANNELS = [
    { value: 'email', label: 'Email', icon: Mail },
    { value: 'sms', label: 'SMS', icon: MessageSquare },
    { value: 'both', label: 'Email + SMS', icon: Send },
];

const CheckList = ({ title, items, selected, onToggle, testId }) => (
    <div className="space-y-2">
        <Label>{title}</Label>
        <div className="border border-[#E5E7EB] rounded-md max-h-44 overflow-y-auto p-2 space-y-1" data-testid={testId}>
            {items.length === 0 && <p className="text-xs text-[#9CA3AF] p-1">None</p>}
            {items.map(it => (
                <label key={it.value} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-[#F9FAFB] rounded px-1 py-0.5">
                    <input type="checkbox" checked={selected.includes(it.value)} onChange={() => onToggle(it.value)} />
                    <span>{it.label}</span>
                </label>
            ))}
        </div>
        {selected.length > 0 && <p className="text-xs text-[#064E3B]">{selected.length} selected</p>}
    </div>
);

const Communications = () => {
    const [programs, setPrograms] = useState([]);
    const [classes, setClasses] = useState([]);
    const [activities, setActivities] = useState([]);
    const [channel, setChannel] = useState('email');
    const [audience, setAudience] = useState('active');
    const [activityId, setActivityId] = useState('');
    const [selBranches, setSelBranches] = useState([]);
    const [selPrograms, setSelPrograms] = useState([]);
    const [selClasses, setSelClasses] = useState([]);
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [preview, setPreview] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [sending, setSending] = useState(false);
    const [history, setHistory] = useState([]);

    const fetchHistory = useCallback(async () => {
        try { const r = await axios.get(`${API_URL}/api/comms/history`, { withCredentials: true }); setHistory(r.data || []); }
        catch { /* silent */ }
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const [p, c, a] = await Promise.all([
                    axios.get(`${API_URL}/api/programs`, { withCredentials: true }),
                    axios.get(`${API_URL}/api/classes`, { withCredentials: true }),
                    axios.get(`${API_URL}/api/activities`, { withCredentials: true }).catch(() => ({ data: [] })),
                ]);
                setPrograms(p.data || []); setClasses(c.data || []); setActivities(a.data || []);
            } catch { toast.error('Failed to load filters'); }
        })();
        fetchHistory();
    }, [fetchHistory]);

    useEffect(() => { setPreview(null); }, [channel, audience, activityId, selBranches, selPrograms, selClasses]);

    const branchOptions = useMemo(() => Array.from(new Set(programs.map(p => p.location).filter(Boolean))).sort().map(b => ({ value: b, label: b })), [programs]);
    const programOptions = useMemo(() => [...programs].sort((a, b) => (a.order ?? 999) - (b.order ?? 999)).map(p => ({ value: p.id, label: `${p.name}${p.location ? ` (${p.location})` : ''}` })), [programs]);
    const classOptions = useMemo(() => classes
        .filter(c => selPrograms.length === 0 || selPrograms.includes(c.program_id))
        .map(c => ({ value: c.id, label: `${c.english_name || c.name || c.code || c.id}${c.arabic_name ? ` · ${c.arabic_name}` : ''}` })), [classes, selPrograms]);
    const toggle = (setter) => (v) => setter(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);

    const body = () => ({ channel, subject, message: message.trim(), audience, branches: selBranches, program_ids: selPrograms, class_ids: audience === 'active' ? selClasses : [], activity_id: audience === 'activity' ? activityId || null : null });
    const needsEmail = channel !== 'sms';
    const canSend = message.trim() && (!needsEmail || subject.trim()) && (audience !== 'activity' || activityId);

    const handlePreview = async () => {
        setPreviewing(true);
        try { const r = await axios.post(`${API_URL}/api/comms/preview`, body(), { withCredentials: true }); setPreview(r.data); }
        catch (e) { toast.error(e.response?.data?.detail || 'Preview failed'); }
        finally { setPreviewing(false); }
    };
    const handleSend = async () => {
        if (!canSend) return;
        if (!window.confirm(`Send this ${channel === 'both' ? 'email + SMS' : channel} to the selected guardians?`)) return;
        setSending(true);
        try {
            const r = await axios.post(`${API_URL}/api/comms/send`, body(), { withCredentials: true });
            const d = r.data;
            const parts = [];
            if (channel !== 'sms') parts.push(`${d.email_sent} emails${d.email_failed ? ` (${d.email_failed} failed)` : ''}`);
            if (channel !== 'email') parts.push(`${d.sms_sent} SMS${d.sms_failed ? ` (${d.sms_failed} failed)` : ''}`);
            toast.success(`Sent to ${d.guardians} guardians — ${parts.join(', ')}`);
            setMessage(''); setSubject(''); setPreview(null); fetchHistory();
        } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
        finally { setSending(false); }
    };

    const smsSegments = message.length === 0 ? 0 : Math.ceil(message.length / 160);

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="communications-page">
                <div>
                    <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit'] flex items-center gap-2"><Send className="h-6 w-6 text-[#064E3B]" />Communications Center</h1>
                    <p className="text-[#4B5563]">Email or SMS both guardians — by branch, program, class, activity, waiting list or pending list</p>
                </div>

                <Tabs defaultValue="compose">
                    <TabsList>
                        <TabsTrigger value="compose" data-testid="tab-comms-compose"><Send className="h-4 w-4 mr-2" />Compose</TabsTrigger>
                        <TabsTrigger value="history" data-testid="tab-comms-history"><History className="h-4 w-4 mr-2" />History ({history.length})</TabsTrigger>
                    </TabsList>

                    <TabsContent value="compose">
                        <Card className="card-surface">
                            <CardHeader><CardTitle className="text-lg font-['Outfit']">Compose message</CardTitle></CardHeader>
                            <CardContent className="space-y-5">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Channel</Label>
                                        <div className="flex gap-2" data-testid="comms-channel">
                                            {CHANNELS.map(c => (
                                                <button key={c.value} type="button" onClick={() => setChannel(c.value)} data-testid={`comms-channel-${c.value}`}
                                                    className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm transition-colors ${channel === c.value ? 'bg-[#064E3B] text-white border-[#064E3B]' : 'bg-white text-[#1F2937] border-[#D1D5DB] hover:bg-[#F9FAFB]'}`}>
                                                    <c.icon className="h-4 w-4" />{c.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Audience</Label>
                                        <Select value={audience} onValueChange={setAudience}>
                                            <SelectTrigger data-testid="comms-audience"><SelectValue /></SelectTrigger>
                                            <SelectContent>{AUDIENCES.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                {audience === 'activity' && (
                                    <div className="space-y-2">
                                        <Label>Activity / event</Label>
                                        <Select value={activityId} onValueChange={setActivityId}>
                                            <SelectTrigger data-testid="comms-activity"><SelectValue placeholder="Select an activity" /></SelectTrigger>
                                            <SelectContent>{activities.map(a => <SelectItem key={a.id} value={a.id}>{a.name}{a.start_date ? ` — ${a.start_date}` : ''}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                )}

                                {audience !== 'activity' && (
                                    <div className={`grid grid-cols-1 gap-4 ${audience === 'active' ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
                                        <CheckList title="Branches (empty = all)" items={branchOptions} selected={selBranches} onToggle={toggle(setSelBranches)} testId="comms-branch-list" />
                                        <CheckList title="Programs (empty = all)" items={programOptions} selected={selPrograms} onToggle={toggle(setSelPrograms)} testId="comms-program-list" />
                                        {audience === 'active' && <CheckList title="Classes (empty = all)" items={classOptions} selected={selClasses} onToggle={toggle(setSelClasses)} testId="comms-class-list" />}
                                    </div>
                                )}

                                {needsEmail && (
                                    <div className="space-y-2">
                                        <Label>Email subject</Label>
                                        <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="e.g. Important update about Saturday classes" data-testid="comms-subject" />
                                    </div>
                                )}
                                <div className="space-y-2">
                                    <Label>Message</Label>
                                    <textarea className="w-full min-h-[140px] rounded-md border border-[#D1D5DB] p-3 text-sm focus:ring-2 focus:ring-[#064E3B] focus:border-transparent"
                                        value={message} onChange={e => setMessage(e.target.value)}
                                        placeholder={channel === 'sms' ? 'Type the SMS guardians will receive…' : 'Type your message. The school logo and signature are added automatically to emails.'}
                                        data-testid="comms-message" />
                                    <p className="text-xs text-[#4B5563]">{message.length} characters{channel !== 'email' ? ` ≈ ${smsSegments} SMS segment${smsSegments === 1 ? '' : 's'} per recipient` : ''}</p>
                                </div>

                                <div className="flex flex-wrap items-center gap-3">
                                    <Button variant="outline" onClick={handlePreview} disabled={previewing} data-testid="comms-preview-btn">
                                        {previewing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}Preview recipients
                                    </Button>
                                    <Button className="bg-[#064E3B] hover:bg-[#047857]" onClick={handleSend} disabled={sending || !canSend} data-testid="comms-send-btn">
                                        {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                                        Send{preview ? ` to ${preview.total_guardians} guardians` : ''}
                                    </Button>
                                </div>

                                {preview && (
                                    <div className="bg-[#FAF9F6] border border-[#E5E7EB] rounded-lg p-4 space-y-2" data-testid="comms-preview-panel">
                                        <div className="flex flex-wrap gap-2">
                                            <Badge variant="secondary"><Users className="h-3 w-3 mr-1" />{preview.total_guardians} guardians matched</Badge>
                                            {channel !== 'sms' && <Badge className="bg-[#064E3B] text-white"><Mail className="h-3 w-3 mr-1" />{preview.email_recipients} email addresses</Badge>}
                                            {channel !== 'email' && <Badge className="bg-[#0F766E] text-white"><MessageSquare className="h-3 w-3 mr-1" />{preview.sms_recipients} mobile numbers</Badge>}
                                        </div>
                                        {preview.sample?.length > 0 && <p className="text-xs text-[#4B5563]">{preview.sample.join(', ')}{preview.total_guardians > preview.sample.length ? ` +${preview.total_guardians - preview.sample.length} more` : ''}</p>}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="history">
                        <Card className="card-surface">
                            <CardContent className="p-4">
                                {history.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-12"><History className="h-12 w-12 text-[#4B5563]/30 mb-4" /><p className="text-[#4B5563]">Nothing sent yet</p></div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <Table>
                                            <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Channel</TableHead><TableHead>Audience</TableHead><TableHead>Subject / message</TableHead><TableHead>Guardians</TableHead><TableHead>Emails</TableHead><TableHead>SMS</TableHead><TableHead>Sent by</TableHead></TableRow></TableHeader>
                                            <TableBody>
                                                {history.map(h => (
                                                    <TableRow key={h.id} data-testid={`comms-history-row-${h.id}`}>
                                                        <TableCell className="text-xs whitespace-nowrap">{formatDateTime(h.created_at)}</TableCell>
                                                        <TableCell><Badge variant="outline" className="capitalize">{h.channel}</Badge></TableCell>
                                                        <TableCell className="text-sm capitalize">{h.audience}</TableCell>
                                                        <TableCell className="text-xs max-w-[320px]">{h.subject && <p className="font-medium">{h.subject}</p>}<span title={h.message}>{h.message?.length > 100 ? `${h.message.slice(0, 100)}…` : h.message}</span></TableCell>
                                                        <TableCell className="tabular-nums">{h.guardians}</TableCell>
                                                        <TableCell className="tabular-nums text-sm">{h.channel === 'sms' ? '—' : `${h.email_sent}${h.email_failed ? ` / ${h.email_failed} failed` : ''}`}</TableCell>
                                                        <TableCell className="tabular-nums text-sm">{h.channel === 'email' ? '—' : `${h.sms_sent}${h.sms_failed ? ` / ${h.sms_failed} failed` : ''}`}</TableCell>
                                                        <TableCell className="text-sm">{h.sent_by_name || '—'}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>
        </DashboardLayout>
    );
};

export default Communications;
